import BaseObserver from './baseObserver'

import { full_url } from "../../url";

const DEFAULT_OFFLINE_POLL_MS = 1000;
const ONLINE_TIME_TO_CHECK_FOR_UPDATES = 30000;

interface OfflineObserverDelegate {
  didGoOnline: () => void;
  didGoOffline: () => void;
}

export default class OfflineObserver extends BaseObserver<OfflineObserverDelegate> {
  testIfOnlineTimer?: NodeJS.Timeout;
  OFFLINE_TIME_TO_CHECK_FOR_UPDATES: number;
  ONLINE_TIME_TO_CHECK_FOR_UPDATES: number;
  noResponseTimer: NodeJS.Timeout;

  constructor(featureFlag?: FeatureFlag) {
    super();

    this.OFFLINE_TIME_TO_CHECK_FOR_UPDATES = featureFlag?.offline_observer_backoff;
    this.ONLINE_TIME_TO_CHECK_FOR_UPDATES = ONLINE_TIME_TO_CHECK_FOR_UPDATES;
  }

  offlinePollIntervalMs() {
    const configured = this.OFFLINE_TIME_TO_CHECK_FOR_UPDATES;
    return configured > 0 ? configured : DEFAULT_OFFLINE_POLL_MS;
  }

  startEventListeners() {
    window.addEventListener("offline", this.notifyOffline.bind(this), true);
    window.addEventListener("online", this.notifyOnline.bind(this), true);
    this.testIfOnlineTimer = setTimeout(this.testIfOnline.bind(this), this.ONLINE_TIME_TO_CHECK_FOR_UPDATES);
  }

  stopEventListeners() {
    window.removeEventListener("offline", this.notifyOffline.bind(this), true);
    window.removeEventListener("online", this.notifyOnline.bind(this), true);
    if (this.testIfOnlineTimer) {
      clearTimeout(this.testIfOnlineTimer);
    }
  }

  afterRegistrationInitializer(delegate: OfflineObserverDelegate) {
    navigator.onLine ? delegate.didGoOnline() : delegate.didGoOffline();
  }

  notifyOffline() {
    this.delegates.forEach(delegate => delegate.didGoOffline());
  }

  notifyOnline() {
    this.delegates.forEach(delegate => delegate.didGoOnline());
  }

  scheduleOfflinePoll() {
    this.testIfOnlineTimer = setTimeout(this.testIfOnline.bind(this), this.offlinePollIntervalMs());
  }

  testIfOnline() {
    if (this.noResponseTimer) {
      clearTimeout(this.noResponseTimer);
    }
    if (this.testIfOnlineTimer) {
      clearTimeout(this.testIfOnlineTimer);
    }

    if (!navigator.onLine) {
      this.notifyOffline();
      this.scheduleOfflinePoll();
      return;
    }

    const xhr = new XMLHttpRequest();

    this.noResponseTimer = setTimeout(() => {
      xhr.abort();
      this.notifyOffline();
      this.scheduleOfflinePoll();
    }, 5000);

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) {
        return;
      }

      clearTimeout(this.noResponseTimer);
      clearTimeout(this.testIfOnlineTimer);
      if (xhr.status === 200) {
        this.notifyOnline();
        this.testIfOnlineTimer = setTimeout(this.testIfOnline.bind(this), this.ONLINE_TIME_TO_CHECK_FOR_UPDATES);
      } else {
        this.notifyOffline();
        this.scheduleOfflinePoll();
      }
    };
    xhr.open("HEAD", full_url("/check/online"), true);
    xhr.send();
  }
}
